import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Flex } from '@chakra-ui/react';
import { Button } from '@morressier/alembic';
import * as Sentry from '@sentry/react';

import { useQuery } from '@tanstack/react-query';
import {
  IExtractedSubmissionMetadata,
  ITitlePageExtractedSubmissionMetadata
} from '@publishing-engine/domain/models';
import { SubmissionFlowLayout } from './components/SubmissionFlowLayout';
import { ISubmissionFormData, useFormContext } from 'src/contexts/FormContext';
import { clientAuthRealm } from 'src/utils/env';
import {
  fetchExtractedSubmissionMetadata,
  fetchExtractedTitlePageMetadata,
  fetchSuggestedKeywords,
  fetchUserProfileDetails
} from 'src/api/queries';
import MetadataExtraction, {
  MetadataExtractionPhase
} from './components/MetadataExtraction';
import ManualInformationModal from './components/ManualInformationModal';
import BackToUploadModal from './components/BackToUploadModal';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { IMetadataExtractionStepConfig } from 'src/hooks/useSubmissionStepsConfig';
interface Props {
  draftSubmissionId: string;
  setMetadataExtracted: (value: boolean) => void;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  autosaveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId: string
  ) => Promise<unknown>;
  stepConfigs: unknown;
}

const MetadataExtractionStep: React.FC<Props> = ({
  setMetadataExtracted,
  goToNextStep,
  autosaveDraftSubmission,
  goToPreviousStep,
  draftSubmissionId,
  stepConfigs
}) => {
  const configs = stepConfigs as IMetadataExtractionStepConfig;
  const [showManualInformationModal, setShowManualInformationModal] =
    useState<boolean>(false);
  const [
    showMismatchManualInformationModal,
    setShowMismatchManualInformationModal
  ] = useState<boolean>(false);
  const [showBackToUploadModal, setShowBackToUploadModal] =
    useState<boolean>(false);
  const [extractionProgress, setExtractionProgress] = useState<number>(0);
  const [titlePageExtractionProgress, setTitlePageExtractionProgress] =
    useState<number>(0);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const { formData, updateFormData } = useFormContext();
  const submissionContextId = formData.submissionContext.submissionContextId;
  const manuscriptUrl = formData.fileSet.Manuscript.url;
  const titlePageUrl = formData.fileSet.TitlePage?.url ?? '';
  const hasHydratedSchema =
    (formData.submissionContext.schema?.formFields?.length ?? 0) > 0;
  const canExtractManuscript = Boolean(
    draftSubmissionId && submissionContextId && manuscriptUrl
  );
  const canExtractTitlePage = Boolean(
    titlePageUrl &&
      configs.TitlePage &&
      draftSubmissionId &&
      submissionContextId
  );
  const [extractionPhase, setExtractionPhase] =
    useState<MetadataExtractionPhase>('started');
  const [titlePageExtractionPhase, setTitlePageExtractionPhase] =
    useState<MetadataExtractionPhase>('started');
  const [keywordSuggestionPhase, setKeywordSuggestionPhase] =
    useState<MetadataExtractionPhase>('started');
  const [keywordSuggestionProgress, setKeywordSuggestionProgress] =
    useState<number>(0);

  const [contentMismatch, setContentMismatch] = useState<boolean>(false);
  const [missingInformation, setMissingInformation] = useState<boolean>(false);

  const [manualOverride, setManualOverride] = useState<boolean>(false);
  const { submissionContext, countriesData } = useSubmissionContextData();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [`submission-extraction-${draftSubmissionId}`, manuscriptUrl],
    queryFn: () =>
      fetchExtractedSubmissionMetadata(
        draftSubmissionId,
        submissionContextId,
        manuscriptUrl
      ),
    enabled: canExtractManuscript,
    retry: 0
  });

  const {
    data: titlePageData,
    isLoading: titlePageLoading,
    isError: titlePageError,
    refetch: titlePageRefetch
  } = useQuery({
    queryKey: [`submission-extraction-${draftSubmissionId}`, titlePageUrl],
    queryFn: () =>
      configs.TitlePage
        ? fetchExtractedTitlePageMetadata(
            draftSubmissionId,
            submissionContextId,
            titlePageUrl
          )
        : null,
    enabled: canExtractTitlePage,
    retry: 0
  });

  const { data: authorData } = useQuery({
    queryKey: ['user-profile-data'],
    queryFn: () => fetchUserProfileDetails()
  });

  const extractedMetadata = data?.extracted_data;
  const extractedTitle = extractedMetadata?.title ?? '';
  const extractedAbstract = Array.isArray(extractedMetadata?.abstract)
    ? extractedMetadata.abstract[0] ?? ''
    : (extractedMetadata?.abstract ?? '');

  const shouldEnrichKeywords =
    clientAuthRealm === 'm' &&
    submissionContextId === '01JV64RKX5M0SB4QFTC45HFJ1T';

  const canSuggestKeywords = Boolean(
    shouldEnrichKeywords &&
      extractionPhase === 'completed' &&
      titlePageExtractionPhase === 'completed' &&
      extractedTitle &&
      extractedAbstract
  );

  const {
    data: suggestedKeywordsData,
    isLoading: keywordLoading,
    isError: keywordError
  } = useQuery({
    queryKey: [
      'suggest-keywords',
      draftSubmissionId,
      submissionContextId,
      extractedTitle,
      extractedAbstract
    ],
    queryFn: () =>
      fetchSuggestedKeywords(
        draftSubmissionId,
        submissionContextId,
        extractedTitle,
        extractedAbstract
      ),
    enabled: canSuggestKeywords,
    retry: 0
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isLoading) {
      setExtractionProgress(0);
      setContentMismatch(false);
      interval = setInterval(() => {
        setExtractionProgress(prev => (prev < 90 ? prev + 1 : prev));
      }, 100); // increase every 100ms
    } else if (data) {
      setContentMismatch(
        Boolean(titlePageUrl) && data.extracted_data?.authors?.length !== 0
      );
      setExtractionProgress(100); // done
    }

    return () => clearInterval(interval);
  }, [isLoading, data, titlePageUrl]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (!titlePageUrl) {
      setTitlePageExtractionProgress(100);
      setTitlePageExtractionPhase('completed');
    } else if (titlePageLoading) {
      setTitlePageExtractionProgress(0);
      interval = setInterval(() => {
        setTitlePageExtractionProgress(prev => (prev < 90 ? prev + 1 : prev));
      }, 100); // increase every 100ms
    } else if (titlePageData) {
      titlePageData.extracted_data?.authors.length === 0 &&
        setMissingInformation(true);
      setTitlePageExtractionProgress(100); // done
    }

    if (!titlePageUrl) {
      setTitlePageExtractionPhase('completed');
    } else if (!hasHydratedSchema) {
      setTitlePageExtractionPhase('started');
    } else if (!titlePageLoading && titlePageData) {
      setTitlePageExtractionPhase('completed');
    } else if (titlePageError) {
      setTitlePageExtractionPhase('failed');
    }

    return () => clearInterval(interval);
  }, [
    titlePageLoading,
    titlePageData,
    titlePageError,
    titlePageUrl,
    hasHydratedSchema
  ]);

  useEffect(() => {
    if (!canSuggestKeywords) {
      setKeywordSuggestionPhase('started');
      setKeywordSuggestionProgress(0);
      return;
    }
    let interval: NodeJS.Timeout;
    if (keywordLoading) {
      setKeywordSuggestionPhase('started');
      setKeywordSuggestionProgress(0);
      interval = setInterval(() => {
        setKeywordSuggestionProgress(prev => (prev < 90 ? prev + 1 : prev));
      }, 100);
    } else if (keywordError) {
      Sentry.captureException(keywordError);
      setKeywordSuggestionPhase('failed');
      setKeywordSuggestionProgress(100);
    } else if (suggestedKeywordsData) {
      setKeywordSuggestionPhase('completed');
      setKeywordSuggestionProgress(100);
    }
    return () => clearInterval(interval);
  }, [canSuggestKeywords, keywordLoading, keywordError, suggestedKeywordsData]);

  const methods = useForm<ISubmissionFormData>({
    defaultValues: formData
  });
  const { handleSubmit } = methods;

  const getFormData = (extractedData?: IExtractedSubmissionMetadata) => {
    const currentUser = authorData?.data;

    const currentUserData = {
      email: currentUser?.user.email ?? '',
      firstName: currentUser?.user?.firstName ?? '',
      lastName: currentUser?.user?.lastName ?? '',
      middleName: currentUser?.user?.middleName ?? '',
      country: currentUser?.affiliationDetails?.country ?? '',
      countryCode: currentUser?.affiliationDetails?.countryCode ?? '',
      city: currentUser?.affiliationDetails?.city ?? '',
      id: currentUser?.user?.id ?? '',
      affiliation: currentUser?.affiliationDetails?.organizationName || '',
      orcid: currentUser?.user?.orcid || '',
      ringgoldId: currentUser?.affiliationDetails?.ringgoldId || ''
    };

    const {
      title = 'Untitled',
      abstract = '',
      keywords = []
    } = extractedData ?? {};
    const finalKeywords =
      Array.isArray(suggestedKeywordsData) && suggestedKeywordsData.length > 0
        ? suggestedKeywordsData
        : keywords;
    const titlePageExtractedData = (titlePageData?.extracted_data ||
      {}) as ITitlePageExtractedSubmissionMetadata;
    const { title: titlePageTitle, authors: titlePageAuthors = [] } =
      titlePageExtractedData;
    const metadataTitle =
      !titlePageTitle || titlePageTitle.trim() === '' ? title : titlePageTitle;
    const extractedAuthors = titlePageUrl
      ? titlePageAuthors
      : extractedData?.authors ?? [];

    const finalAuthors = extractedAuthors
      .filter(author => author.email !== currentUser?.user.email)
      .map(author => {
        const country = author?.country?.[0] ?? '';
        const countryCode = countriesData?.find(
          item => item.country.toLowerCase() === country.toLowerCase()
        )?.countryCode;
        return {
          email: author.email ?? '',
          firstName: author?.first_name ?? '',
          lastName: author?.last_name ?? '',
          middleName: author?.middle_name ?? '',
          affiliation: author.institution?.[0] ?? '',
          country: country,
          countryCode: countryCode ?? '',
          city: author?.city?.[0] ?? ''
        };
      });

    const filteredAuthors = finalAuthors.filter(author =>
      Object.values(author).some(value => value!.trim() !== '')
    );

    const processedAbstract = Array.isArray(abstract)
      ? abstract[0] || ''
      : abstract;
    const sanitizedFileSet = titlePageUrl
      ? formData.fileSet
      : (({ TitlePage: _TitlePage, ...fileSet }) => fileSet)(formData.fileSet);

    return {
      ...formData,
      fileSet: sanitizedFileSet,
      keywords: finalKeywords.map(keyword => ({ text: keyword })),
      metadata: {
        ...formData.metadata,
        title: metadataTitle,
        abstract: processedAbstract,
        keywords: finalKeywords,
        ...(configs.Authors
          ? {
              authors: [currentUserData, ...filteredAuthors]
            }
          : {})
      },
      submittingAuthor: currentUserData.email,
      ...(configs.CorrespondingAuthor
        ? {
            correspondingAuthor: currentUserData.email
          }
        : {}),
      ...(configs.ContactAuthor
        ? {
            contactAuthor: currentUserData.email
          }
        : {})
    };
  };

  const handleManualInformation = () => {
    const formData = getFormData();
    updateFormData(formData);
    goToNextStep();
  };

  const onSubmit = () => {
    // Prevent multiple clicks
    if (isNavigating) return;

    setIsNavigating(true);
    const extractedData = data?.extracted_data as
      | IExtractedSubmissionMetadata
      | undefined;

    const newFormData = getFormData(extractedData);

    // set extracted metadata as form fields
    updateFormData(newFormData);
    setMetadataExtracted(true);

    autosaveDraftSubmission(newFormData, draftSubmissionId).then(() => {
      goToNextStep();
      setIsNavigating(false);
    });
  };

  useEffect(() => {
    if (!canExtractManuscript) {
      setExtractionPhase('started');
    } else if (!isLoading && data) {
      setExtractionPhase('completed');
    } else if (isError) {
      setExtractionPhase('failed');
    }
  }, [canExtractManuscript, isLoading, data, isError]);

  // Timer to show button after 1 minute
  useEffect(() => {
    const timer = setTimeout(() => {
      setManualOverride(true);
    }, 60000);

    return () => clearTimeout(timer);
  }, []);

  const retryFetchMetadataExtraction = () => {
    refetch();
    titlePageRefetch();
    setExtractionPhase('started');
    setTitlePageExtractionPhase('started');
    setManualOverride(false);
  };
  const getCombinedStatus = (): MetadataExtractionPhase => {
    if (titlePageUrl) {
      if (extractionPhase === 'failed' || titlePageExtractionPhase === 'failed')
        return 'failed';
      if (
        extractionPhase === 'completed' &&
        titlePageExtractionPhase === 'completed'
      )
        return 'completed';
      return 'started';
    } else {
      return extractionPhase;
    }
  };

  const createEmptySubmissionFile = (
    referenceName: 'Manuscript' | 'TitlePage'
  ) => ({
    referenceName,
    name: '',
    extension: '',
    url: ''
  });

  const onDeleteDraft = async () => {
    updateFormData({
      fileSet: {
        ...formData.fileSet,
        Manuscript: createEmptySubmissionFile('Manuscript'),
        TitlePage: createEmptySubmissionFile('TitlePage')
      }
    });
    setShowBackToUploadModal(false);
    goToPreviousStep();
  };
  const FooterButtons = () => {
    const combinedStatus = getCombinedStatus();
    if (contentMismatch) {
      return (
        <Button
          variant="primary"
          onClick={() => setShowBackToUploadModal(true)}
          width={{ base: '100%', md: 'auto' }}
        >
          Back to Upload
        </Button>
      );
    } else if (missingInformation) {
      return (
        <Flex
          gap={2}
          justifyContent="space-between"
          width="100%"
          flexDirection={{ base: 'column', md: 'row' }}
        >
          <Button
            variant="secondary"
            onClick={() => setShowBackToUploadModal(true)}
            disabled={
              extractionPhase === 'started' ||
              titlePageExtractionPhase === 'started'
            }
            width={{ base: '100%', md: 'auto' }}
          >
            Back to Upload
          </Button>
          <Flex
            gap={2}
            justifyContent="flex-end"
            flexDirection={{ base: 'column', sm: 'row' }}
            width={{ base: '100%', md: 'auto' }}
          >
            <Button
              variant="primary"
              onClick={() => setShowMismatchManualInformationModal(true)}
              disabled={
                extractionPhase === 'started' ||
                titlePageExtractionPhase === 'started'
              }
              width={{ base: '100%', sm: 'auto' }}
            >
              Add Information Manually
            </Button>
          </Flex>
        </Flex>
      );
    } else {
      switch (combinedStatus) {
        case 'started': {
          return (
            <Flex
              justifyContent={'space-between'}
              width={'full'}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: 2, md: 0 }}
            >
              <Button
                variant="secondary"
                disabled={!manualOverride || isNavigating}
                onClick={() => setShowBackToUploadModal(true)}
                width={{ base: '100%', md: 'auto' }}
              >
                {'Back to Upload'}
              </Button>
              <Button
                variant="primary"
                disabled={!manualOverride || isNavigating}
                onClick={() => setShowManualInformationModal(true)}
                width={{ base: '100%', md: 'auto' }}
              >
                {manualOverride ? 'Add Information Manually' : 'Next'}
              </Button>
            </Flex>
          );
        }
        case 'completed': {
          return (
            <Button
              variant="primary"
              type="submit"
              alignSelf="flex-start"
              marginLeft="auto"
              disabled={isNavigating || (canSuggestKeywords && keywordLoading)}
              width={{ base: '100%', md: 'auto' }}
            >
              Next
            </Button>
          );
        }
        case 'failed': {
          return (
            <Flex
              gap={2}
              justifyContent="space-between"
              width="100%"
              flexDirection={{ base: 'column', md: 'row' }}
            >
              <Button
                variant="secondary"
                onClick={() => setShowBackToUploadModal(true)}
                disabled={
                  extractionPhase === 'started' ||
                  titlePageExtractionPhase === 'started'
                }
                width={{ base: '100%', md: 'auto' }}
              >
                Back to Upload
              </Button>
              <Flex
                gap={2}
                justifyContent="flex-end"
                flexDirection={{ base: 'column', sm: 'row' }}
                width={{ base: '100%', md: 'auto' }}
              >
                <Button
                  variant="secondary"
                  onClick={() => setShowManualInformationModal(true)}
                  disabled={
                    extractionPhase === 'started' ||
                    titlePageExtractionPhase === 'started'
                  }
                  width={{ base: '100%', sm: 'auto' }}
                >
                  Add Information Manually
                </Button>
                <Button
                  variant="primary"
                  onClick={retryFetchMetadataExtraction}
                  disabled={
                    extractionPhase === 'started' ||
                    titlePageExtractionPhase === 'started'
                  }
                  width={{ base: '100%', sm: 'auto' }}
                >
                  Retry
                </Button>
              </Flex>
            </Flex>
          );
        }
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <SubmissionFlowLayout width="100%">
          <Box width="100%" maxWidth="960px" mx="auto" px={{ base: 3, md: 0 }}>
            <MetadataExtraction
              phase={extractionPhase}
              manualOverride={manualOverride}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              onRetry={retryFetchMetadataExtraction}
              metadataExtraction={
                submissionContext?.config.cms.authorSubmissionForm
                  .metadataExtraction
              }
              extractionProgress={extractionProgress}
              titlePageExtractionProgress={titlePageExtractionProgress}
              titlePageExtractionPhase={titlePageExtractionPhase}
              titlePageFile={!!titlePageUrl}
              contentMismatch={contentMismatch}
              missingInformation={missingInformation}
              TitlePageData={titlePageData?.extracted_data?.authors ?? []}
              MenuscriptData={data?.extracted_data?.authors ?? []}
              keywordSuggestionEnabled={shouldEnrichKeywords}
              keywordSuggestionPhase={keywordSuggestionPhase}
              keywordSuggestionProgress={keywordSuggestionProgress}
            />
          </Box>
        </SubmissionFlowLayout>

        {/* ----- Footer Buttons ----- */}
        <Flex
          paddingY={{ base: 3, md: 4 }}
          px={{ base: 3, md: 0 }}
          bottom={0}
          zIndex={1}
          justifyContent="center"
          position="sticky"
          bgColor="surfacePrimaryFill"
          marginX="auto"
        >
          <Flex
            width="100%"
            maxWidth="960px"
            justifyContent="space-between"
            alignItems="center"
          >
            <FooterButtons />
            <ManualInformationModal
              isOpen={
                showManualInformationModal || showMismatchManualInformationModal
              }
              onClose={() =>
                showMismatchManualInformationModal
                  ? setShowMismatchManualInformationModal(false)
                  : setShowManualInformationModal(false)
              }
              onSave={
                showMismatchManualInformationModal
                  ? onSubmit
                  : handleManualInformation
              }
            />
            <BackToUploadModal
              isOpen={showBackToUploadModal}
              onClose={() => setShowBackToUploadModal(false)}
              onSave={onDeleteDraft}
            />
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default MetadataExtractionStep;
